import { produce } from 'immer';
import { arrayToggleInclude } from 'lib/riskbase';
import { cloneDeep, difference, union } from 'lodash';
import { RESET_DEV_DATA, SIGN_OUT } from 'store/auth/actions';
import * as actions from 'store/remote/actions';
import {
  AssessmentLibrary,
  AssessmentSuggestions,
  PageEnum,
  RemoteState,
} from 'store/remote/types';
import {
  ReportEnum,
  reportAssetsReducer,
  reportCheckCompletedReducer,
  reportCheckScheduleReducer,
  reportCheckStatusReducer,
  reportElementsReducer,
} from 'types/Reports';
import {
  EngageFlatsDefaultFilter,
  EngagePropertiesDefaultFilter,
  EngageScheduledVisitListItem,
} from 'types/Engage';
import { createReducer } from 'typesafe-actions';
import {
  doEngageClearData,
  doEngageContactUpdate,
  doEngageFlatCommunicationsLoad,
  doEngageFlatCommunicationsLoadFailed,
  doEngageFlatCommunicationsSetData,
  doEngageFlatLoad,
  doEngageFlatsUnload,
  doEngageFlatSetData,
  doEngageFlatsLoad,
  doEngageFlatsSetData,
  doEngageFlatUpdateData,
  doEngagePropertiesLoad,
  doEngagePropertiesSetData,
  doEngagePropertiesSetFilter,
  doEngageSetLoadState,
  doEngageFlatArchivedContactsSet,
  doEngageFlatAssessmentSummariesLoad,
  doEngageFlatAssessmentSummariesLoadFailed,
  doEngageFlatAssessmentSummariesSetData,
  doEngageFlatsSetFilter,
  doEngageScheduledVisitsSetData,
  doEngageScheduledVisitRecipientsLoad,
  doEngageScheduledVisitRecipientsUnload,
  doEngageScheduledVisitRecipientsSetData,
  doEngageScheduledVisitTemplatesLoad,
  doEngageScheduledVisitTemplatesSet,
  doEngageScheduledVisitCommunicationScheduleLoad,
  doEngageScheduledVisitCommunicationScheduleUnload,
  doEngageScheduledVisitCommunicationScheduleSet,
  doEngageScheduledVisitCommunicationScheduleSendNowRequested,
  doEngageContactInteractionLogAdd,
  doEngageScheduledVisitsLoad,
  doEngageScheduledVisitsUnload,
  doEngageFlatScheduledVisitAvailabilitySet,
} from './engageActions';
import {
  flatContactToFlatListContact,
  getDefaultEngageData,
  sortContactsByType,
} from './engageUtils';
import {
  SettingsAssessmentType,
  SettingsComplianceType,
} from 'types/ComplianceAndAssessmentTypeSettings';

const initialState: RemoteState = {
  usage: 0,
  assessmentLibraryOpened: {
    sectionId: undefined,
    questionId: undefined,
    checklistItemId: undefined,
    actionId: undefined,
    controlId: undefined,
    answerId: undefined,
  },
  assessmentTemplateOpened: {
    templateId: undefined,
  },
  assessmentLibrarySettings: {
    actionFilter: {
      search: '',
    },
  },
  aseLibraryCollapsedSections: [],
  fieldSettingsOpened: {
    sectionId: undefined,
    groupId: undefined,
    fieldId: undefined,
    liteGroupId: undefined,
  },
  complianceTypeSettings: {
    complianceTypes: undefined,
    assessmentTypes: undefined,
  },
  reporting: {
    loadStatus: undefined,
    reports: {},
  },

  pagesSaved: [],
  pages: [],
  data: {},
};

export const remoteReducer = createReducer(initialState)
  .handleAction(SIGN_OUT.success, () => initialState)
  .handleAction(RESET_DEV_DATA, (state) => ({
    ...initialState,
    pagesSaved: state.pagesSaved,
  }))

  // Resetting data after usage
  .handleAction(
    [
      actions.doSettingPageClose,
      actions.doSetupAseClosed,
      actions.REMOTE_ASESETUP_CLOSED,
      actions.doFieldSettingUnload,
      actions.doSetupBillingClosed,
      actions.doSetupBillingAdminClosed,
      actions.doSetupApiClose,
      actions.doSetupEngageClose,
      actions.doSetupSsoClose,
      actions.doSetupOrganisationClose,
    ],
    (state) =>
      state.usage > 1
        ? {
            ...state,
            usage: Math.max(state.usage - 1, 0),
          }
        : {
            ...initialState,
            usage: Math.max(state.usage - 1, 0),
            assessmentLibrarySettings: state.assessmentLibrarySettings,
            aseLibraryCollapsedSections: state.aseLibraryCollapsedSections,
            reporting: state.reporting,
            pages: state.pages,
            pagesSaved: state.pagesSaved,
            data: state.data,
          },
  )

  // Setup
  .handleAction(actions.doRemoteSetOrganisation, (state, action) => ({
    ...state,
    organisationId: action.payload,
  }))
  // Field Setup
  .handleAction(actions.doFieldSettingsInit, (state) => ({
    ...state,
    usage: state.usage + 1,
  }))
  .handleAction(actions.doFieldSettingsSetData, (state, action) => ({
    ...state,
    organisationFieldSettings: action.payload,
  }))
  .handleAction(actions.doFieldSettingsSetResultCount, (state, action) => ({
    ...state,
    organisationFieldResultCount: action.payload,
  }))
  .handleAction(actions.doFieldSettingsSetSectionId, (state, action) => ({
    ...state,
    fieldSettingsOpened: {
      ...state.fieldSettingsOpened,
      sectionId: action.payload,
      groupId: undefined,
      fieldId: undefined,
    },
  }))
  .handleAction(actions.doFieldSettingsSetGroupId, (state, action) => ({
    ...state,
    fieldSettingsOpened: {
      ...state.fieldSettingsOpened,
      groupId: action.payload,
      fieldId: undefined,
    },
  }))
  .handleAction(actions.doFieldSettingsSetFieldId, (state, action) => ({
    ...state,
    fieldSettingsOpened: {
      ...state.fieldSettingsOpened,
      fieldId: action.payload,
    },
  }))
  .handleAction(actions.doFieldSettingsSetLiteGroupId, (state, action) => ({
    ...state,
    fieldSettingsOpened: {
      ...state.fieldSettingsOpened,
      liteGroupId: action.payload,
    },
  }))
  .handleAction([actions.doFieldSettingsLoad.success], (state, action) =>
    produce(state, (draft) => {
      draft.organisationFieldSettings = action.payload.fieldSettings;
    }),
  )

  // Settings - Assessment - Checks

  .handleAction(actions.doSettingsAseChecksSetData, (state, action) => ({
    ...state,
    usage: state.usage + 1,
    settingsAssessmentChecks: action.payload.checks,
    complianceTypes: action.payload.complianceTypes,
    complianceSubtypes: action.payload.complianceSubtypes,
  }))

  .handleAction(actions.doSettingsAseChecksMergeData, (state, action) => ({
    ...state,
    settingsAssessmentChecks:
      action.payload.checks || state.settingsAssessmentChecks,
    complianceTypes: action.payload.complianceTypes || state.complianceTypes,
    complianceSubtypes:
      action.payload.complianceSubtypes || state.complianceSubtypes,
  }))

  // Assessment Setup Template
  .handleAction(actions.doSetupAseSetData, (state, action) => ({
    ...state,
    usage: state.usage + 1,
    assessmentTemplates: action.payload.templates,
    assessmentTemplateDefaultCosts: action.payload.templateDefaultCosts,
    complianceTypes: action.payload.complianceTypes,
    complianceSubtypes: action.payload.complianceSubtypes,
    assessmentSchemes: action.payload.schemes,
    assessmentPdfs: action.payload.pdfs,
    assessmentFlags: action.payload.flags,
    assessmentSignatories: action.payload.signatories,
    assessmentValidityTimescales: action.payload.validityTimescales,
    organisation: action.payload.organisation,
    customers: action.payload.customers,
    assessmentLibrary: {
      folders: action.payload.library.folders,
      sections: action.payload.library.sections,
      questions: action.payload.library.questions,
      answers: {},
      checklistItems: {},
      actionDefinitions: action.payload.library.actionDefinitions,
    },
    fieldSectionIdsByOrganisation: action.payload.fieldSectionIdsByOrganisation,
    fieldSections: action.payload.fieldSections,
    fieldGroups: action.payload.fieldGroups,
    fieldFields: action.payload.fieldFields,
    fieldGroupCheckTypes: action.payload.fieldGroupCheckTypes,
  }))
  .handleAction(actions.doSetupAseMergeData, (state, action) => {
    const opened = state.assessmentTemplateOpened;
    const templates = state.assessmentTemplates!;
    const library = state.assessmentLibrary!;
    const flags = state.assessmentFlags!;
    const signatories = state.assessmentSignatories!;
    return {
      ...state,
      assessmentTemplateOpened: !action.payload.opened
        ? opened
        : {
            ...opened,
            ...action.payload.opened,
          },
      assessmentTemplates: !action.payload.templates
        ? templates
        : {
            ...templates,
            ...action.payload.templates,
          },
      assessmentLibrary: !action.payload.library
        ? library
        : {
            ...library,
            questions: !action.payload.library.questions
              ? library.questions
              : action.payload.library.questions,
          },
      assessmentFlags: action.payload.flags || flags,
      assessmentSignatories: action.payload.signatories || signatories,
    };
  })
  .handleAction(actions.doSetupAseRemoveData, (state, action) =>
    produce(state, (draft) => {
      action.payload.templates?.forEach((deleteId) => {
        delete draft.assessmentTemplates![deleteId];
      });
    }),
  )
  .handleAction(actions.doAseSetupTemplateOpen, (state, action) => ({
    ...state,
    assessmentTemplateOpened: {
      ...state.assessmentTemplateOpened,
      templateId: action.payload.id,
    },
  }))
  .handleAction(actions.doAseSetupTemplateClosed, (state) => ({
    ...state,
    assessmentTemplateOpened: {
      ...state.assessmentTemplateOpened,
      templateId: undefined,
    },
  }))

  // Assessment Setup Library
  .handleAction(actions.REMOTE_ASESETUP_SET, (state, action) => ({
    ...state,
    usage: state.usage + 1,
    categories: action.payload.categories,
    categoryGroups: action.payload.categoryGroups,
    severities: action.payload.severities,
    timescales: action.payload.timescales,
    dueTypes: action.payload.dueTypes,
    assessmentSuggestions: action.payload.suggestions,
    assessmentLibrary: action.payload.library,
  }))
  .handleAction(actions.REMOTE_ASESETUP_REMOVE, (state, action) =>
    produce(state, (draft) => {
      if (action.payload.library) {
        Object.entries(action.payload.library).forEach(
          ([libKey, deleteIds]) => {
            const idKey = `${libKey.slice(
              0,
              -1,
            )}Id` as keyof typeof draft.assessmentLibraryOpened;
            deleteIds.forEach((id) => {
              if (draft.assessmentLibraryOpened[idKey] === id) {
                draft.assessmentLibraryOpened[idKey] = undefined;
              }
              delete draft.assessmentLibrary![
                libKey as keyof Omit<
                  AssessmentLibrary,
                  'questionId' | 'checklistItemId'
                >
              ][id];
            });
          },
        );
      }
      if (action.payload.suggestions) {
        Object.entries(action.payload.suggestions).forEach(
          ([libKey, deleteIds]) => {
            deleteIds.forEach((id) => {
              delete draft.assessmentSuggestions![
                libKey as keyof AssessmentSuggestions
              ][id];
            });
          },
        );
      }
    }),
  )
  .handleAction(actions.REMOTE_ASESETUP_MERGE, (state, action) => {
    const opened = state.assessmentLibraryOpened;
    const settings = state.assessmentLibrarySettings;
    const library = state.assessmentLibrary!;
    const suggestions = state.assessmentSuggestions!;
    return {
      ...state,
      assessmentLibraryOpened: !action.payload.opened
        ? opened
        : {
            ...opened,
            ...action.payload.opened,
          },
      assessmentLibrarySettings: !action.payload.settings
        ? settings
        : {
            ...settings,
            ...action.payload.settings,
          },
      assessmentSuggestions: !action.payload.suggestions
        ? suggestions
        : {
            ...suggestions,
            ...action.payload.suggestions,
            actions: action.payload.suggestions.actions
              ? {
                  ...suggestions.actions,
                  ...action.payload.suggestions.actions,
                }
              : suggestions.actions,
            controls: action.payload.suggestions.controls
              ? {
                  ...suggestions.controls,
                  ...action.payload.suggestions.controls,
                }
              : suggestions.controls,
          },
      assessmentLibrary: !action.payload.library
        ? library
        : {
            ...library,
            ...action.payload.library,
            folders: !action.payload.library.folders
              ? library.folders
              : {
                  ...library.folders,
                  ...action.payload.library.folders,
                },
            sections: !action.payload.library.sections
              ? library.sections
              : {
                  ...library.sections,
                  ...action.payload.library.sections,
                },
            questions: !action.payload.library.questions
              ? library.questions
              : {
                  ...library.questions,
                  ...action.payload.library.questions,
                },
            answers: !action.payload.library.answers
              ? library.answers
              : {
                  ...library.answers,
                  ...action.payload.library.answers,
                },
            checklistItems: !action.payload.library.checklistItems
              ? library.checklistItems
              : {
                  ...library.checklistItems,
                  ...action.payload.library.checklistItems,
                },
          },
    };
  })
  .handleAction(actions.REMOTE_ASESETUP_SECTIONOPEN, (state, action) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      sectionId: action.payload.id,
    },
  }))
  .handleAction(
    actions.REMOTE_ASESETUPLIBRARY_QUESTIONOPEN,
    (state, action) => ({
      ...state,
      assessmentLibraryOpened: {
        ...state.assessmentLibraryOpened,
        questionId: action.payload.id,
      },
    }),
  )
  .handleAction(actions.REMOTE_ASESETUP_QUESTIONCLOSED, (state) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      questionId: undefined,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_CHECKLISTITEMOPEN, (state, action) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      checklistItemId: action.payload.id,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_ANSWEROPEN, (state, action) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      answerId: action.payload.id,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_ANSWERCLOSED, (state) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      answerId: undefined,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_CHECKLISTITEMCLOSED, (state) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      checklistItemId: undefined,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_SECTIONCLOSED, (state) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      sectionId: undefined,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_ACTIONCLOSED, (state) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      actionId: undefined,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_ACTIONOPEN, (state, action) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      actionId: action.payload.id,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_CONTROLOPEN, (state, action) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      controlId: action.payload.id,
    },
  }))
  .handleAction(actions.REMOTE_ASESETUP_CONTROLCLOSED, (state) => ({
    ...state,
    assessmentLibraryOpened: {
      ...state.assessmentLibraryOpened,
      controlId: undefined,
    },
  }))
  .handleAction(
    actions.REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS,
    (state, action) => {
      const sectionIds = Object.values(state.assessmentLibrary?.sections || [])
        .filter((section) => section.folderId === action.payload.folderId)
        .map((section) => section.id);
      return {
        ...state,
        aseLibraryCollapsedSections: action.payload.collapse
          ? union(state.aseLibraryCollapsedSections, sectionIds)
          : difference(state.aseLibraryCollapsedSections, sectionIds),
      };
    },
  )
  .handleAction(
    actions.REMOTE_ASESETUP_TOGGLESECTIONCOLLAPSED,
    (state, action) => ({
      ...state,
      aseLibraryCollapsedSections: arrayToggleInclude(
        state.aseLibraryCollapsedSections,
        action.payload,
      ),
    }),
  )
  // Billing
  .handleAction(actions.doSetupBillingAdminSetData, (state, action) => ({
    ...state,
    usage: action.payload.addToUsage ? state.usage + 1 : state.usage,
    billingOrganisation:
      action.payload.organisation ?? state.billingOrganisation,
    billingProducts: action.payload.products ?? state.billingProducts,
    billingProductsUnits:
      action.payload.productUnits ?? state.billingProductsUnits,
    billingItems: action.payload.items ?? state.billingItems,
    billingDiscounts: action.payload.discounts ?? state.billingDiscounts,
    customers: action.payload.customers ?? state.customers,
  }))
  .handleAction(actions.doSetupBillingSetData, (state, action) => ({
    ...state,
    usage: action.payload.addToUsage ? state.usage + 1 : state.usage,
    billingDraftInvoices:
      action.payload.draftInvoices ?? state.billingDraftInvoices,
    billingInvoices: action.payload.invoices ?? state.billingInvoices,
    billingStatement: action.payload.statement ?? state.billingStatement,
  }))
  // Reporting
  .handleAction(actions.doReportingSetLoadingStatus, (state, action) => ({
    ...state,
    reporting: {
      ...state.reporting,
      loadStatus: action.payload,
    },
  }))
  .handleAction(actions.doReportingSetSavedPages, (state, action) =>
    produce(state, (draft) => {
      // const current = state.reporting.pages.find((p) => p.current);
      const existingPagesKeyedById = state.pagesSaved.reduce<
        Record<string, (typeof draft.pagesSaved)[number]>
      >((acc, page) => {
        acc[page.id] = page;
        return acc;
      }, {});
      // let alreadyHasCurrent = false;
      draft.pagesSaved = action.payload.pages.map((page) => {
        const existingPage = existingPagesKeyedById[page.id];
        return {
          ...page,
          key: page.key || page.id,
          closeable: page.closeable ?? true,
          loaded: existingPage?.loaded ?? false,
        };
      });
    }),
  )
  .handleAction(actions.doReportingAddPage, (state, action) =>
    produce(state, (draft) => {
      if (!draft.pages.find((page) => page.key === action.payload.key)) {
        draft.pages.push(action.payload);
      }
    }),
  )
  .handleAction(actions.doReportingClosePage, (state, action) =>
    produce(state, (draft) => {
      const foundIndex = draft.pages.findIndex(
        (page) => page.key === action.payload.key,
      );
      if (foundIndex !== -1) {
        draft.pages.splice(foundIndex, 1);
      }
      // Blindly delete key from reports - closing page may not be a report
      delete draft.reporting.reports[action.payload.key];
    }),
  )
  // .handleAction(actions.doReportingSetReport, (state, action) => ({
  //   ...state,
  //   reporting: {
  //     ...state.reporting,
  //     report: {
  //       loaded: false,
  //       isLoading: 0,
  //       key: action.payload.key,
  //       id: action.payload.id,
  //       name: action.payload.name,
  //       description: action.payload.description,
  //       type: action.payload.type,
  //       typeName: action.payload.typeName,
  //       typeIsDefault: action.payload.typeIsDefault,
  //       filterOptions: action.payload.filterOptions,
  //       filterValues: {}, // action.payload.filterValues,
  //       draftFilterValues: action.payload.filterValues,
  //       data: {},
  //       order: [],
  //       totalCount: 0,
  //       pageNumber: 0,
  //       visuals: [],
  //     },
  //   },
  // }))
  .handleAction(actions.doReportingSetReport, (state, action) =>
    produce(state, (draft) => {
      draft.reporting.reports[action.payload.key] = {
        loaded: false,
        isLoading: 0,
        key: action.payload.key,
        id: action.payload.id,
        name: action.payload.name,
        description: action.payload.description,
        menuDescription: action.payload.menuDescription,
        icon: action.payload.icon,
        type: action.payload.type,
        typeName: action.payload.typeName,
        typeIsDefault: action.payload.typeIsDefault,
        filterOptions: action.payload.filterOptions,
        // filterValues: {}, // action.payload.filterValues,
        filterValues: action.payload.filterValues,
        draftFilterValues: action.payload.filterValues,
        data: {},
        order: [],
        totalCount: 0,
        pageNumber: 0,
        visuals: [],
      };
    }),
  )
  .handleAction(actions.doReportingMergeDraftFilterValues, (state, action) =>
    produce(state, (draft) => {
      const report = draft.reporting.reports[action.payload.key];
      if (report) {
        report.draftFilterValues = {
          ...report.draftFilterValues,
          ...action.payload.filterValues,
        };
      }
    }),
  )
  .handleAction(actions.doReportingResetDraftFilterValues, (state, action) =>
    produce(state, (draft) => {
      const report = draft.reporting.reports[action.payload.key];
      if (report) {
        report.draftFilterValues = cloneDeep(report.filterValues);
      }
    }),
  )
  .handleAction(actions.doReportingSetFilterValues, (state, action) =>
    produce(state, (draft) => {
      const report = draft.reporting.reports[action.payload.key];
      if (report) {
        report.filterValues = action.payload.filterValues;
      }
    }),
  )
  .handleAction(actions.doReportingSetDataListLoading, (state, action) =>
    produce(state, (draft) => {
      const report = draft.reporting.reports[action.payload.key];
      if (report) {
        report.isLoading += action.payload.loading ? 1 : -1;
      }
    }),
  )

  .handleAction(actions.doReportingSetDataVisualRaw, (state, action) =>
    produce(state, (draft) => {
      const report = draft.reporting.reports[action.payload.key];
      if (report) {
        report.visuals = action.payload.response?.visuals || [];
      }
    }),
  )

  .handleAction(actions.doReportingMergeDataListRaw, (state, action) =>
    produce(state, (draft) => {
      const report = draft.reporting.reports[action.payload.key];
      if (!report) {
        return;
      }
      const { merge, response, pageNumber } = action.payload;
      if (
        report.type === ReportEnum.checkschedule &&
        response.type === ReportEnum.checkschedule
      ) {
        const { data, order } = reportCheckScheduleReducer(response);
        report.data = merge ? { ...report.data, ...data } : data;
        report.order = merge
          ? [...report.order, ...difference(order, report.order)]
          : order;
      } else if (
        report.type === ReportEnum.checkscompleted &&
        response.type === ReportEnum.checkscompleted
      ) {
        const { data, order } = reportCheckCompletedReducer(response);
        report.data = merge ? { ...report.data, ...data } : data;
        report.order = merge
          ? [...report.order, ...difference(order, report.order)]
          : order;
      } else if (
        report.type === ReportEnum.checkstatus &&
        response.type === ReportEnum.checkstatus
      ) {
        const { data, order } = reportCheckStatusReducer(response);
        report.data = merge ? { ...report.data, ...data } : data;
        report.order = merge
          ? [...report.order, ...difference(order, report.order)]
          : order;
      } else if (
        report.type === ReportEnum.elements &&
        response.type === ReportEnum.elements
      ) {
        const { data, order } = reportElementsReducer(response);
        report.data = merge ? { ...report.data, ...data } : data;
        report.order = merge
          ? [...report.order, ...difference(order, report.order)]
          : order;
      } else if (
        report.type === ReportEnum.assets &&
        response.type === ReportEnum.assets
      ) {
        const { data, order } = reportAssetsReducer(response);
        report.data = merge ? { ...report.data, ...data } : data;
        report.order = merge
          ? [...report.order, ...difference(order, report.order)]
          : order;
      } else {
        // eslint-disable-next-line no-console
        console.warn('Report Data Not Setup');
        report.data = {};
        report.order = [];
      }
      report.loaded = true;
      report.pageNumber = pageNumber;
      report.totalCount = response.totalCount;
    }),
  )
  // API
  .handleAction(actions.doSetupApiSetData, (state, action) => ({
    ...state,
    usage: action.payload.addToUsage ? state.usage + 1 : state.usage,
    apiUsers: action.payload.users,
    apiWebhooks: action.payload.webhooks,
  }))
  // Engage
  .handleAction(actions.doSetupEngageSetData, (state, action) => ({
    ...state,
    usage: action.payload.addToUsage ? state.usage + 1 : state.usage,
    engage: action.payload.engage,
  }))
  /**
   * SSO
   */
  .handleAction(actions.doSetupSsoSetData, (state, action) => ({
    ...state,
    usage: action.payload.addToUsage ? state.usage + 1 : state.usage,
    ssoSettings: action.payload.data,
  }))
  // Organisation
  .handleAction(actions.doSetupOrganisationSetData, (state, action) => ({
    ...state,
    usage: action.payload.addToUsage ? state.usage + 1 : state.usage,
    setupOrganisation: action.payload.setupOrganisation,
    customers: action.payload.customers,
  }))
  .handleAction(actions.doSetupOrganisationUpdateData, (state, action) => ({
    ...state,
    setupOrganisation: action.payload.setupOrganisation,
  }))

  /**
   * Engage
   */
  .handleAction(doEngageClearData, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      delete draft.data[key];
    }),
  )
  .handleAction(doEngageSetLoadState, (state, action) =>
    produce(state, (draft) => {
      const { key, loading, section } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data[section].isLoading = loading;
      // If loading is false then it has loaded
      if (!loading) {
        dataSet.data[section].isLoaded = true;
      }
    }),
  )
  .handleAction(doEngagePropertiesLoad, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.properties.isLoading = true;
    }),
  )
  .handleAction(doEngagePropertiesSetData, (state, action) =>
    produce(state, (draft) => {
      const { key, response, pageNumber, clearData } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.properties.isLoading = false;
      dataSet.data.properties.isLoaded = true;
      dataSet.data.properties.totalCount = response.totalCount;
      dataSet.data.properties.pageNumber = pageNumber;
      // Add property data
      dataSet.data.properties.data = response.properties.reduce(
        (acc, property) => {
          const organisation =
            response.expandedData.organisations[property.organisationId];
          acc[property.id] = {
            ...property,
            organisationName: organisation?.name || 'Unknown',
          };
          return acc;
        },
        clearData ? {} : dataSet.data.properties.data,
      );
      // Add any new properties to order
      dataSet.data.properties.dataOrder = clearData
        ? response.properties.map((p) => p.id)
        : [
            ...new Set([
              ...dataSet.data.properties.dataOrder,
              ...response.properties.map((p) => p.id),
            ]),
          ];
    }),
  )
  .handleAction(doEngagePropertiesSetFilter, (state, action) =>
    produce(state, (draft) => {
      const { key, data, replace } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      const nextFilters = replace
        ? { ...EngagePropertiesDefaultFilter, ...data }
        : { ...dataSet.data.properties.filters, ...data };
      dataSet.data.properties.filters = nextFilters;
    }),
  )
  .handleAction(doEngageFlatsSetFilter, (state, action) =>
    produce(state, (draft) => {
      const { key, data, replace } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      const nextFilters = replace
        ? { ...EngageFlatsDefaultFilter, ...data }
        : { ...dataSet.data.flats.filters, ...data };
      dataSet.data.flats.filters = nextFilters;
    }),
  )
  .handleAction(doEngageFlatsLoad, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.flats.isLoading = true;
    }),
  )
  .handleAction(doEngageFlatsSetData, (state, action) =>
    produce(state, (draft) => {
      const { key, response, pageNumber, clearData } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.flats.isLoading = false;
      dataSet.data.flats.isLoaded = true;
      dataSet.data.flats.totalCount = response.totalCount;
      dataSet.data.flats.pageNumber = pageNumber;
      // Add property data
      dataSet.data.flats.data = response.flats.reduce(
        (acc, flat) => {
          acc[flat.id] = {
            id: flat.id,
            customReference: flat.customReference,
            name: flat.name,
            reference: flat.reference,
            imageUrl: flat.imageUrl,
            surveyResponseDate: flat.surveyResponseDate,
            occupationStatus: flat.occupationStatus,
            contacts: sortContactsByType(flat.contacts),
            isHMO: flat.isHMO,
            isCommerciallyOwned: flat.isCommerciallyOwned,
            issues: {
              hasNoResidents: flat.issues.hasNoResidents,
              hasNoOwners: flat.issues.hasNoOwners,
              isMissingResidents: flat.issues.isMissingResidents,
              hasExpiredResidentConfirmations:
                flat.issues?.hasExpiredResidentConfirmations,
              hasExpiredOwnerConfirmations:
                flat.issues?.hasExpiredOwnerConfirmations,
              hasOwnerIssues: flat.issues.hasOwnerIssues,
              hasResidentIssues: flat.issues.hasResidentIssues,
              hasEmailIssues: flat.issues.hasEmailIssues,
              requiresContactSurvey: flat.issues.requiresContactSurvey,
              hasSurveyResponseOverdue: flat.issues.hasSurveyResponseOverdue,
            },
            confirmations: {
              oldestResidentConfirmationDate:
                flat.confirmations.oldestResidentConfirmationDate,
              oldestOwnerConfirmationDate:
                flat.confirmations.oldestOwnerConfirmationDate,
              residentConfirmationExpiryDate:
                flat.confirmations.residentConfirmationExpiryDate,
              ownerConfirmationExpiryDate:
                flat.confirmations.ownerConfirmationExpiryDate,
            },
            matchingContactIds: flat.matchingContactIds,
          };
          return acc;
        },
        clearData ? {} : dataSet.data.flats.data,
      );
      // Add any new properties to order
      dataSet.data.flats.dataOrder = clearData
        ? response.flats.map((p) => p.id)
        : [
            ...new Set([
              ...dataSet.data.flats.dataOrder,
              ...response.flats.map((p) => p.id),
            ]),
          ];
    }),
  )
  .handleAction(doEngageFlatsUnload, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.flats = getDefaultEngageData().data.flats;
      dataSet.data.bulkCommunication = null;
    }),
  )
  .handleAction(doEngageFlatLoad, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
    }),
  )
  .handleAction(doEngageFlatSetData, (state, action) =>
    produce(state, (draft) => {
      const { key, property, flat, response } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.flat = {
        property: property,
        flatDetails: flat,
        contacts: sortContactsByType(response.contacts),
        archivedContacts: null,
        archivedContactsCount: response.archivedContactsCount ?? 0,
        communications: [],
        communicationsInitialLoadFailed: false,
        communicationsLoading: false,
        communicationsTotalCount: 0,
        communicationsPageNumber: 0,
        assessmentSummaries: [],
        assessmentSummariesLoading: false,
        assessmentSummariesInitialLoadFailed: false,
        assessmentSummariesTotalCount: 0,
        assessmentSummariesPageNumber: 0,
        scheduledVisits: response.scheduledVisits,
      };
      const listItem = dataSet.data.flats.data[flat.id];
      if (listItem) {
        listItem.contacts = sortContactsByType(response.contacts).map(
          flatContactToFlatListContact,
        );
      }
    }),
  )
  .handleAction(doEngageFlatArchivedContactsSet, (state, action) =>
    produce(state, (draft) => {
      const { key, contacts } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat) {
        return;
      }
      dataSet.data.flat.archivedContacts = sortContactsByType(contacts);
      dataSet.data.flat.archivedContactsCount = contacts.length;
    }),
  )
  .handleAction(doEngageFlatCommunicationsLoad, (state, action) =>
    produce(state, (draft) => {
      const { key, flatId, propertyId, page } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat) {
        return;
      }
      if (
        dataSet.data.flat.flatDetails.id !== flatId ||
        dataSet.data.flat.property.id !== propertyId
      ) {
        return;
      }
      if (page === 'reset') {
        dataSet.data.flat.communicationsInitialLoadFailed = false;
      }
      dataSet.data.flat.communicationsLoading = true;
    }),
  )
  .handleAction(doEngageFlatCommunicationsSetData, (state, action) =>
    produce(state, (draft) => {
      const { key, flatId, propertyId, response, clearData, pageNumber } =
        action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat) {
        return;
      }
      if (
        dataSet.data.flat.flatDetails.id !== flatId ||
        dataSet.data.flat.property.id !== propertyId
      ) {
        return;
      }
      dataSet.data.flat.communications = clearData
        ? response.communications
        : [...dataSet.data.flat.communications, ...response.communications];
      dataSet.data.flat.communicationsInitialLoadFailed = false;
      dataSet.data.flat.communicationsLoading = false;
      dataSet.data.flat.communicationsTotalCount = response.totalCount;
      dataSet.data.flat.communicationsPageNumber = pageNumber;
    }),
  )
  .handleAction(doEngageFlatCommunicationsLoadFailed, (state, action) =>
    produce(state, (draft) => {
      const { key, flatId, propertyId, page } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat) {
        return;
      }
      if (
        dataSet.data.flat.flatDetails.id !== flatId ||
        dataSet.data.flat.property.id !== propertyId
      ) {
        return;
      }
      if (page === 'reset') {
        dataSet.data.flat.communicationsInitialLoadFailed = true;
      }
      dataSet.data.flat.communicationsLoading = false;
    }),
  )
  .handleAction(doEngageFlatAssessmentSummariesLoad, (state, action) =>
    produce(state, (draft) => {
      const { key, flatId, propertyId, page } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat) {
        return;
      }
      if (
        dataSet.data.flat.flatDetails.id !== flatId ||
        dataSet.data.flat.property.id !== propertyId
      ) {
        return;
      }
      if (page === 'reset') {
        dataSet.data.flat.assessmentSummariesInitialLoadFailed = false;
      }
      dataSet.data.flat.assessmentSummariesLoading = true;
    }),
  )
  .handleAction(doEngageFlatAssessmentSummariesSetData, (state, action) =>
    produce(state, (draft) => {
      const { key, flatId, propertyId, response, clearData, pageNumber } =
        action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat) {
        return;
      }
      if (
        dataSet.data.flat.flatDetails.id !== flatId ||
        dataSet.data.flat.property.id !== propertyId
      ) {
        return;
      }
      dataSet.data.flat.assessmentSummaries = clearData
        ? response.assessmentSummaries
        : [
            ...dataSet.data.flat.assessmentSummaries,
            ...response.assessmentSummaries,
          ];
      dataSet.data.flat.assessmentSummariesInitialLoadFailed = false;
      dataSet.data.flat.assessmentSummariesLoading = false;
      dataSet.data.flat.assessmentSummariesTotalCount = response.totalCount;
      dataSet.data.flat.assessmentSummariesPageNumber = pageNumber;
    }),
  )
  .handleAction(doEngageFlatAssessmentSummariesLoadFailed, (state, action) =>
    produce(state, (draft) => {
      const { key, flatId, propertyId, page } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat) {
        return;
      }
      if (
        dataSet.data.flat.flatDetails.id !== flatId ||
        dataSet.data.flat.property.id !== propertyId
      ) {
        return;
      }
      if (page === 'reset') {
        dataSet.data.flat.assessmentSummariesInitialLoadFailed = true;
      }
      dataSet.data.flat.assessmentSummariesLoading = false;
    }),
  )
  .handleAction(doEngageFlatUpdateData, (state, action) =>
    produce(state, (draft) => {
      const {
        key,
        contacts,
        archivedContactIds,
        unarchivedContactIds,
        occupationStatus,
        isHMO,
        isCommerciallyOwned,
        issues,
      } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat) {
        return;
      }
      const flatListItem =
        dataSet.data.flats.data[dataSet.data.flat.flatDetails.id];

      // Handle flat data
      if (occupationStatus !== undefined) {
        dataSet.data.flat.flatDetails.occupationStatus = occupationStatus;
        if (flatListItem) {
          flatListItem.occupationStatus = occupationStatus;
        }
      }
      if (isHMO !== undefined) {
        dataSet.data.flat.flatDetails.isHMO = isHMO;
        if (flatListItem) {
          flatListItem.isHMO = isHMO;
        }
      }
      if (isCommerciallyOwned !== undefined) {
        dataSet.data.flat.flatDetails.isCommerciallyOwned = isCommerciallyOwned;
        if (flatListItem) {
          flatListItem.isCommerciallyOwned = isCommerciallyOwned;
        }
      }
      if (issues !== undefined) {
        dataSet.data.flat.flatDetails.issues = issues;
        if (flatListItem) {
          flatListItem.issues = issues;
        }
      }

      // Handle unarchived contacts
      if (unarchivedContactIds?.length && dataSet.data.flat.archivedContacts) {
        // Get contacts to unarchive
        const contactsToUnarchive = dataSet.data.flat.archivedContacts.filter(
          (contact) => unarchivedContactIds.includes(contact.id),
        );
        if (contactsToUnarchive.length) {
          dataSet.data.flat.archivedContactsCount = Math.max(
            dataSet.data.flat.archivedContactsCount -
              contactsToUnarchive.length,
            0,
          );
          // Add to contacts
          dataSet.data.flat.contacts = sortContactsByType([
            ...dataSet.data.flat.contacts,
            ...contactsToUnarchive,
          ]);
          // Remove from archivedContacts
          dataSet.data.flat.archivedContacts =
            dataSet.data.flat.archivedContacts.filter(
              (contact) => !unarchivedContactIds.includes(contact.id),
            );
          // Add to flat list
          if (flatListItem) {
            flatListItem.contacts = sortContactsByType([
              ...flatListItem.contacts,
              ...contactsToUnarchive.map(flatContactToFlatListContact),
            ]);
          }
        }
      }

      // Handle updated contacts
      if (contacts?.length) {
        // Update contacts in flat
        const updatedContactsMap = Object.fromEntries(
          contacts.map((c) => [c.id, c]),
        );
        dataSet.data.flat.contacts = sortContactsByType(
          dataSet.data.flat.contacts.map((c) => updatedContactsMap[c.id] || c),
        );
        // Update contacts in flat list
        if (flatListItem) {
          const updatedFlatListContactsMap = Object.fromEntries(
            contacts.map((c) => [c.id, flatContactToFlatListContact(c)]),
          );
          flatListItem.contacts = sortContactsByType(
            flatListItem.contacts.map(
              (c) => updatedFlatListContactsMap[c.id] || c,
            ),
          );
        }
      }

      // Handle archived contacts
      if (archivedContactIds?.length) {
        // Get contacts to archive
        const contactsToArchive = dataSet.data.flat.contacts.filter((contact) =>
          archivedContactIds.includes(contact.id),
        );
        if (contactsToArchive.length) {
          dataSet.data.flat.archivedContactsCount += contactsToArchive.length;
          // Add to archivedContacts
          if (dataSet.data.flat.archivedContacts) {
            dataSet.data.flat.archivedContacts = sortContactsByType([
              ...dataSet.data.flat.archivedContacts,
              ...contactsToArchive,
            ]);
          }
          // Remove from contacts
          dataSet.data.flat.contacts = dataSet.data.flat.contacts.filter(
            (contact) => !archivedContactIds.includes(contact.id),
          );
        }
        // Remove from flat list
        if (flatListItem) {
          flatListItem.contacts = flatListItem.contacts.filter(
            (contact) => !archivedContactIds.includes(contact.id),
          );
        }
      }
    }),
  )
  .handleAction(doEngageFlatScheduledVisitAvailabilitySet, (state, action) =>
    produce(state, (draft) => {
      const { key, flatId, scheduledVisitId, isUnavailable, slots } =
        action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat || dataSet.data.flat.flatDetails.id !== flatId) {
        return;
      }

      const scheduledVisit = dataSet.data.flat.scheduledVisits.find(
        (visit) => visit.id === scheduledVisitId,
      );
      if (!scheduledVisit) {
        return;
      }

      const slotResponsesById = new Map(
        slots.map((slot) => [slot.id, slot.hasResponse]),
      );
      scheduledVisit.isUnavailable = isUnavailable;
      scheduledVisit.slots = scheduledVisit.slots.map((slot) => ({
        ...slot,
        hasResponse: slotResponsesById.get(slot.id) ?? slot.hasResponse,
      }));
    }),
  )

  .handleAction(doEngageContactUpdate, (state, action) =>
    produce(state, (draft) => {
      const { key, isNew, flatId, propertyId, contact } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }

      // Add to noOfContacts in properties (listing)
      if (isNew) {
        const property = dataSet.data.properties.data[propertyId];
        if (property) {
          property.noOfContacts += 1;
        }
      }

      // Add to flats (listing)
      const flatListItem = dataSet.data.flats.data[flatId];
      if (flatListItem) {
        flatListItem.contacts = sortContactsByType([
          ...flatListItem.contacts.filter((c) => c.id !== contact.id),
          flatContactToFlatListContact(contact),
        ]);
      }

      // Add to flat
      if (dataSet.data.flat?.flatDetails.id === flatId) {
        dataSet.data.flat.contacts = sortContactsByType([
          ...dataSet.data.flat.contacts.filter((c) => c.id !== contact.id),
          { ...contact },
        ]);
      }
    }),
  )
  .handleAction(doEngageContactInteractionLogAdd, (state, action) =>
    produce(state, (draft) => {
      const { key, flatId, contactId, log } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.flat || dataSet.data.flat.flatDetails.id !== flatId) {
        return;
      }

      const currentContact = dataSet.data.flat.contacts.find(
        (contact) => contact.id === contactId,
      );

      if (!currentContact) {
        return;
      }

      currentContact.interactionLogs = [
        log,
        ...(currentContact.interactionLogs ?? []),
      ];
    }),
  )
  .handleAction(doEngageScheduledVisitTemplatesLoad, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }
      if (!dataSet.data.scheduledVisitTemplates) {
        dataSet.data.scheduledVisitTemplates = {
          status: 'loading',
          templates: [],
        };
      } else {
        dataSet.data.scheduledVisitTemplates.status = 'loading';
      }
    }),
  )
  .handleAction(doEngageScheduledVisitTemplatesSet, (state, action) =>
    produce(state, (draft) => {
      const { key, templates } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.scheduledVisitTemplates = {
        status: 'loaded',
        templates,
      };
    }),
  )
  .handleAction(
    doEngageScheduledVisitCommunicationScheduleLoad,
    (state, action) =>
      produce(state, (draft) => {
        const { key } = action.payload;
        let dataSet = draft.data[key];
        if (!dataSet) {
          draft.data[key] = getDefaultEngageData();
          dataSet = draft.data[key];
        }
        if (dataSet.type !== PageEnum.engage) {
          return;
        }
        dataSet.data.scheduledVisitCommunicationSchedule = {
          status: 'loading',
          communicationSchedule: [],
        };
      }),
  )
  .handleAction(
    doEngageScheduledVisitCommunicationScheduleSet,
    (state, action) =>
      produce(state, (draft) => {
        const { key, communicationSchedule } = action.payload;
        let dataSet = draft.data[key];
        if (!dataSet) {
          draft.data[key] = getDefaultEngageData();
          dataSet = draft.data[key];
        }
        if (dataSet.type !== PageEnum.engage) {
          return;
        }
        dataSet.data.scheduledVisitCommunicationSchedule = {
          status: 'loaded',
          communicationSchedule,
        };
      }),
  )
  .handleAction(
    doEngageScheduledVisitCommunicationScheduleSendNowRequested,
    (state, action) =>
      produce(state, (draft) => {
        const { key, templateType, sendNowRequestedDate } = action.payload;
        let dataSet = draft.data[key];
        if (!dataSet) {
          draft.data[key] = getDefaultEngageData();
          dataSet = draft.data[key];
        }
        if (dataSet.type !== PageEnum.engage) {
          return;
        }
        const communicationSchedule =
          dataSet.data.scheduledVisitCommunicationSchedule
            ?.communicationSchedule;
        if (!communicationSchedule) {
          return;
        }

        const communication = communicationSchedule.find(
          (item) => item.templateType === templateType,
        );
        if (!communication) {
          return;
        }

        communication.sendNowRequestedDate = sendNowRequestedDate;
      }),
  )
  .handleAction(
    doEngageScheduledVisitCommunicationScheduleUnload,
    (state, action) =>
      produce(state, (draft) => {
        const { key } = action.payload;
        const dataSet = draft.data[key];
        if (!dataSet || dataSet.type !== PageEnum.engage) {
          return;
        }
        dataSet.data.scheduledVisitCommunicationSchedule = {
          status: 'notloaded',
          communicationSchedule: [],
        };
      }),
  )

  .handleAction(doEngageScheduledVisitsLoad, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.scheduledVisits.isLoading = true;
    }),
  )
  .handleAction(doEngageScheduledVisitsUnload, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      dataSet.data.scheduledVisits =
        getDefaultEngageData().data.scheduledVisits;
    }),
  )
  .handleAction(doEngageScheduledVisitsSetData, (state, action) =>
    produce(state, (draft) => {
      const { key, response } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }

      const scheduledVisits = response.scheduledVisits;
      const formattedScheduledVisits: Record<
        string,
        EngageScheduledVisitListItem
      > = {};
      const dataOrder: string[] = [];
      for (const scheduledVisit of scheduledVisits) {
        formattedScheduledVisits[scheduledVisit.id] = {
          id: scheduledVisit.id,
          startDateTime: scheduledVisit.startDateTime,
          endDateTime: scheduledVisit.endDateTime,
          publishedDate: scheduledVisit.publishedDate,
          cancelledDate: scheduledVisit.cancelledDate,
          cancelledReason: scheduledVisit.cancelledReason,
          status: scheduledVisit.status,
          historic: scheduledVisit.historic,
          slots: scheduledVisit.slots ?? [],
          inspectors: scheduledVisit.inspectors,
        };
        dataOrder.push(scheduledVisit.id);
      }

      dataSet.data.scheduledVisits.isLoading = false;
      dataSet.data.scheduledVisits.isLoaded = true;
      dataSet.data.scheduledVisits.totalCount = scheduledVisits.length;
      dataSet.data.scheduledVisits.data = formattedScheduledVisits;
      dataSet.data.scheduledVisits.dataOrder = dataOrder;
    }),
  )
  .handleAction(doEngageScheduledVisitRecipientsLoad, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;
      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }
      // Wipe previous data + loading
      dataSet.data.scheduledVisitRecipients = {
        loading: true,
        data: null,
      };
    }),
  )

  .handleAction(doEngageScheduledVisitRecipientsSetData, (state, action) =>
    produce(state, (draft) => {
      const { key, data } = action.payload;

      let dataSet = draft.data[key];
      if (!dataSet) {
        draft.data[key] = getDefaultEngageData();
        dataSet = draft.data[key];
      }
      if (dataSet.type !== PageEnum.engage) {
        return;
      }
      // Set the data
      dataSet.data.scheduledVisitRecipients = {
        loading: false,
        data,
      };
    }),
  )
  .handleAction(doEngageScheduledVisitRecipientsUnload, (state, action) =>
    produce(state, (draft) => {
      const { key } = action.payload;

      const dataSet = draft.data[key];
      if (!dataSet || dataSet.type !== PageEnum.engage) {
        return;
      }
      // Clear data from store when modal closes
      dataSet.data.scheduledVisitRecipients = {
        loading: false,
        data: null,
      };
    }),
  )
  .handleAction(
    actions.doSettingsComplianceAndAssessmentTypesSetData,
    (state, action) => ({
      ...state,
      usage: state.usage + 1,
      complianceTypeSettings: {
        complianceTypes: action.payload.complianceTypes.reduce<
          Record<string, SettingsComplianceType>
        >((acc, type) => {
          acc[type.id] = type;
          return acc;
        }, {}),
        assessmentTypes: action.payload.assessmentTypes.reduce<
          Record<string, SettingsAssessmentType>
        >((acc, type) => {
          acc[type.id] = type;
          return acc;
        }, {}),
      },
    }),
  )
  .handleAction(actions.doSettingsComplianceTypeUpdate, (state, action) => ({
    ...state,
    complianceTypeSettings: {
      complianceTypes: {
        ...state.complianceTypeSettings?.complianceTypes,
        [action.payload.resource.id]: action.payload.resource,
      },
      assessmentTypes: state.complianceTypeSettings?.assessmentTypes || {},
    },
  }))
  .handleAction(actions.doSettingsAssessmentTypeUpdate, (state, action) => ({
    ...state,
    complianceTypeSettings: {
      complianceTypes: state.complianceTypeSettings?.complianceTypes || {},
      assessmentTypes: {
        ...state.complianceTypeSettings?.assessmentTypes,
        [action.payload.resource.id]: action.payload.resource,
      },
    },
  }));
